import { cvProjectsAPI, cvRolesAPI, cvPersonSkillsAPI, cvIndustriesAPI, cvAPI } from 'api/CvAPI'
import { IItemDraftHandle } from 'types/hocTypes'
import React, { useEffect, useMemo, useState } from 'react'
import { ICv, IEmployer, IPersonIndustry, IPersonRole, IPersonSkill, IProject } from 'types/cvsInterfaces'
import withProfileCard from 'Components/reusable/HOC/withProfileCard'
import { getProjectInitialData } from 'Components/reusable/DataContext/InitialData'
import { projectSchema } from 'Components/reusable/DataContext/ValidationSchema'
import ProjectItemCard from './ProjectItemCard'
import ProjectModal from './ProjectModal'
import { useTranslation } from 'react-i18next'
import { IError } from 'types/error'

/** @notExported */
interface IProjectsCardProps {
  /** If true, project card can be edited. */
  editable: boolean
  /** CV object. */
  cv: ICv
  /** Function to set updatedAt. */
  setUpdatedAt: () => void
  /** Function to replace or add item. */
  replaceOrAddItem: (newItem: IProject | { deleted: number }) => IProject[]
  /** Function to open item. */
  openItem: (item: IProject) => void
  /** Current edited item. */
  editedItem: IProject | null
  /** Function to close modal. */
  closeItem: () => void
  /** List of already added projects. */
  items: IProject[]
  /** Header text of the card. */
  header: string
  /** Function to set skills. */
  setSkills: (items: IPersonSkill[]) => void
  /** Function to set industries. */
  setIndustries: (items: IPersonIndustry[]) => void
  /** Function to set roles. */
  setRoles: (items: IPersonRole[]) => void
  /** Function to update overview data. */
  updateOverviewData: (type: string, data: unknown | unknown[]) => void
  /** If true, project card is anonymous. */
  anonymous?: boolean
  /** Function to update CV progress. */
  updateProgress: () => void
  /** Dialog drafts from the CV wizard (see `withProfileCard`'s `itemDraft`). */
  itemDraft?: IItemDraftHandle
  /** Function to update employers. */
  updateEmployers: (newEmployer: IEmployer) => void
  /** Function to update projects list */
  setProjects: (projects: IProject[]) => void
}

/**
 * Card for displaying CV projects.
 *
 * @returns Project card component.
 * @notExported
 */
const ProjectsCard: React.FC<IProjectsCardProps> = ({
  cv,
  editable,
  setUpdatedAt,
  closeItem,
  replaceOrAddItem,
  openItem,
  editedItem,
  items,
  header,
  setSkills,
  setIndustries,
  setRoles,
  updateOverviewData,
  anonymous = false,
  updateProgress,
  itemDraft,
  updateEmployers,
  setProjects,
}) => {
  const { t } = useTranslation()
  const [updateConnected, setUpdateConnected] = useState<number>()

  useEffect(() => {
    const controller = new AbortController()

    if (updateConnected) {
      ;(async () => {
        const skills = await cvPersonSkillsAPI.getList(updateConnected, controller)
        const industries = await cvIndustriesAPI.getList(updateConnected, controller)
        const roles = await cvRolesAPI.getList(updateConnected, controller)
        {
          setSkills(skills)
          setIndustries(industries)
          setRoles(roles)
          setUpdateConnected(undefined)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [setIndustries, setRoles, setSkills, updateConnected])

  const convertToEmployer = async (item: IProject) => {
    try {
      const result = await cvAPI.convertToEmployer(item.id)
      {
        setUpdatedAt()
        updateOverviewData('employers', replaceOrAddItem({ deleted: item.id }))
        updateProgress()
        setUpdateConnected(item.cvId)
        updateEmployers(result)
        const newProjects = await cvProjectsAPI.getList(item.cvId)
        setProjects(newProjects)
        closeItem()
      }
    } catch (error) {
      const err = error as IError
      if (err.name !== 'CanceledError') {
        console.error(err)
      }
    }
  }

  const duplicateProject = async (item: IProject) => {
    const result = await cvProjectsAPI.create(cv.id, {
      ...item,
      id: undefined,
      translations: item.translations.map(tr => ({ ...tr, id: undefined })),
    })
    {
      setUpdatedAt()
      updateOverviewData('projects', replaceOrAddItem(result))
      updateProgress()
      setUpdateConnected(result.cvId)
      closeItem()
    }
  }

  const { i18n } = useTranslation()

  const itemCards = useMemo(
    () =>
      items.map((item, i) => (
        <ProjectItemCard
          key={`${item.id}-${i18n.language}`}
          profileWarningId={`profile-item-projects-${item.id}`}
          editable={editable}
          project={item}
          onOpen={() => openItem(item)}
          header={header}
          anonymous={anonymous}
          showDivider={i < items.length - 1}
        />
      )),
    [anonymous, editable, header, items, openItem, i18n.language]
  )

  return (
    <>
      {itemCards}
      {editedItem !== undefined && (
        <ProjectModal
          key={editedItem?.id ?? 'new'}
          item={editedItem}
          draft={itemDraft?.forItem(editedItem ?? null)}
          cvId={cv.id}
          localeBase="profile.project.modal"
          schema={projectSchema()}
          initialData={getProjectInitialData(cv.id)}
          maxWidth="lg"
          fullWidth
          api={cvProjectsAPI}
          onClose={(value?: Record<string, unknown> | { id?: number; name?: string } | { newItem?: unknown }) => {
            const newItem = (value as { newItem?: unknown } | undefined)?.newItem as
              | IProject
              | { deleted: number; item?: IProject; cvId?: number }
              | undefined
            if (newItem && (('deleted' in newItem && newItem.deleted) || ('id' in newItem && newItem.id))) {
              const items = replaceOrAddItem(newItem)
              setUpdatedAt()
              setProjects(items)
              updateOverviewData('projects', items)
              setUpdateConnected(newItem.cvId)
              updateProgress()
            }
            closeItem()
          }}
          header={header}
          noEscClose
          extraButtonAction={(item: { id?: number; name?: string }) => convertToEmployer(item as IProject)}
          extraButtonLabel={t('profile.project.model.convertToEmployer')}
          duplicateFunction={(item: { id?: number; name?: string }) => duplicateProject(item as IProject)}
        />
      )}
    </>
  )
}

export default withProfileCard(ProjectsCard as unknown as Parameters<typeof withProfileCard>[0])
